import React, { useEffect, useMemo } from 'react';

import { Animated, StyleSheet, ViewStyle } from 'react-native';

import { SharedValue } from 'react-native-reanimated';

import { DrawerNavigationProp } from '@react-navigation/drawer';
import { StackNavigationProp } from '@react-navigation/stack';
import { TransitionSpec } from '@react-navigation/stack/lib/typescript/src/types';
import { SceneProgress } from '@react-navigation/stack/src/types';

import { RouteNavigationParams } from 'router/types';

import useDebouncedSafeAreaInsets from 'utils/hooks/useDebouncedSafeAreaInsets';
import { HEADER_HEIGHT } from 'utils/hooks/useHeaderHeight';

import { underlayingPlayerModalElementsDataSet } from 'screens/PlayerModal/hooks/useModalAccessibility.ts';

import RootNavigationHeaderLeft from 'components/NavigationHeader/RootNavigationHeader/Left';
import RootNavigationHeaderRight from 'components/NavigationHeader/RootNavigationHeader/Right';

import AnimatedHeaderBackground from '../AnimatedHeaderBackground';
import RootNavigationHeaderCenter from './Center';
import styles from './styles';

type Props = {
    navigation:
        | DrawerNavigationProp<any>
        | StackNavigationProp<
              RouteNavigationParams,
              keyof RouteNavigationParams
          >;
    style: ViewStyle;
    pageScrollPosition?: SharedValue<number>;
    progress?: SceneProgress;
    disableFocusOnMount?: boolean;
};

const animationConfig: TransitionSpec = {
    animation: 'timing',
    config: {
        duration: 300,
    },
};

export default function RootNavigationHeader({
    navigation,
    style,
    pageScrollPosition,
    progress,
    disableFocusOnMount,
}: Props) {
    const opacity = Animated.add(
        progress!.current,
        progress!.next || 0,
    ).interpolate({
        inputRange: [0, 1, 1.8, 2],
        outputRange: [0, 1, 0.6, 0],
    });

    const insets = useDebouncedSafeAreaInsets();

    const topPadding = useMemo(() => {
        return Math.max(insets.top, Math.max(insets.left, insets.right));
    }, [insets]);

    useEffect(() => {
        navigation.setOptions({
            transitionSpec: {
                open: animationConfig,
                close: {
                    animation: 'timing',
                    config: {
                        duration: 0,
                    },
                },
            },
        });
    }, []);

    return (
        <Animated.View
            style={[
                styles.headerContainer,
                {
                    paddingTop: topPadding,
                    height: topPadding + HEADER_HEIGHT,
                    opacity,
                },
            ]}
            dataSet={underlayingPlayerModalElementsDataSet}
        >
            {pageScrollPosition && (
                <AnimatedHeaderBackground
                    scrollPosition={pageScrollPosition}
                    style={[StyleSheet.absoluteFillObject, style]}
                />
            )}
            <RootNavigationHeaderLeft
                disableFocusOnMount={disableFocusOnMount}
            />
            <RootNavigationHeaderCenter />
            <RootNavigationHeaderRight navigation={navigation} />
        </Animated.View>
    );
}
